import { useReducer } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import type { CrmRecordSummary } from "@/lib/api/crm/masters";
import { useMoveCrmRecordMaster } from "@/lib/api/crm/records";
import { MoveCrmRecordMasterRequestValidation } from "@/lib/validation/crm";
import { RoutePath } from "../../routerPaths";
import { describeMoveRecordError } from "../crmErrors";
import { recordTypeLabels } from "../recordTypes";
import { MasterCombobox } from "./MasterCombobox";
import { RecordTypeBadge } from "./RecordTypeBadge";

type MoveRecordMasterState = {
  targetMasterId: string;
  reason: string;
  error: string | null;
};

type MoveRecordMasterAction =
  | { type: "changeTargetMaster"; targetMasterId: string }
  | { type: "changeReason"; reason: string }
  | { type: "moveFailed"; error: string };

const initialMoveRecordMasterState: MoveRecordMasterState = { targetMasterId: "", reason: "", error: null };

function moveRecordMasterReducer(
  state: MoveRecordMasterState,
  action: MoveRecordMasterAction,
): MoveRecordMasterState {
  switch (action.type) {
    case "changeTargetMaster":
      return { ...state, targetMasterId: action.targetMasterId, error: null };
    case "changeReason":
      return { ...state, reason: action.reason, error: null };
    case "moveFailed":
      return { ...state, error: action.error };
  }
}

/**
 * Developer only: moves a top-level record, with everything under it, to another master record.
 * Mount it only while open: it loads every master record for the picker.
 */
export function MoveRecordMasterDialog({
  onClose,
  record,
  descendants,
}: {
  onClose: () => void;
  record: CrmRecordSummary;
  /** Every record under `record`; they move with it. */
  descendants: CrmRecordSummary[];
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, dispatch] = useReducer(moveRecordMasterReducer, initialMoveRecordMasterState);
  const moveMutation = useMoveCrmRecordMaster();

  const isMoving = moveMutation.isLoading;
  const canSubmit = state.targetMasterId !== "" && state.reason.trim() !== "" && !isMoving;

  const handleMove = async (): Promise<void> => {
    const parsed = MoveCrmRecordMasterRequestValidation.safeParse({
      record_id: record.id,
      target_master_record_id: state.targetMasterId,
      expected_revision: record.revision,
      reason: state.reason,
    });
    if (!parsed.success) {
      dispatch({ type: "moveFailed", error: parsed.error.issues[0]?.message ?? "Check the form and try again." });
      return;
    }

    try {
      const result = await moveMutation.mutateAsync(parsed.data);
      if (!result.success) {
        dispatch({ type: "moveFailed", error: describeMoveRecordError(result) });
        return;
      }
      toast({
        title: `Moved ${record.long_name}`,
        description: `Now in ${result.data.target_master.long_name}.`,
      });
      onClose();
      navigate(generatePath(RoutePath.CrmRecord, { recordId: record.id }));
    } catch (error: unknown) {
      dispatch({
        type: "moveFailed",
        error: error instanceof Error ? error.message : "The record couldn't be moved. Try again.",
      });
    }
  };

  return (
    <Dialog open onOpenChange={(nextOpen: boolean) => { if (!nextOpen && !isMoving) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Move to different master record</DialogTitle>
          <DialogDescription>
            Moves {record.long_name} ({recordTypeLabels[record.record_type]}) and everything under it to another
            master record. Its contacts, addresses and history move with it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {descendants.length > 0 && (
            <div className="grid gap-2">
              <Label>Also moving</Label>
              <ul className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border bg-gray-50 p-3 text-sm">
                {descendants.map((descendant) => (
                  <li key={descendant.id} className="flex items-center gap-2">
                    <RecordTypeBadge type={descendant.record_type} />
                    <span className="truncate text-gray-800">{descendant.long_name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="crm-move-target-master">Move to</Label>
            <MasterCombobox
              id="crm-move-target-master"
              isModal
              popoverClassName="w-[590px] max-h-72"
              placeholder="Choose a master record"
              excludeMasterId={record.master_record_id}
              value={state.targetMasterId}
              onValueChange={(targetMasterId: string) => dispatch({ type: "changeTargetMaster", targetMasterId })}
              disabled={isMoving}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="crm-move-reason">Reason</Label>
            <Textarea
              id="crm-move-reason"
              placeholder="Why is this record moving?"
              value={state.reason}
              onChange={(event) => dispatch({ type: "changeReason", reason: event.target.value })}
              disabled={isMoving}
              data-testid="crm-move-reason"
            />
          </div>

          {state.error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="crm-move-error">
              {state.error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isMoving}>Cancel</Button>
          <Button type="button" onClick={handleMove} disabled={!canSubmit} data-testid="crm-move-submit">
            {isMoving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRightLeft className="mr-2 h-4 w-4" />}
            {isMoving ? "Moving…" : "Move record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
